import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import './LandingPage.css';
import { WhatsAppBusinessAnimation } from '../components/WhatsAppBusinessAnimation';
import { WhatsAppChatAnimation } from '../components/WhatsAppChatAnimation';
import { useAuth } from '../contexts/AuthContext';

import { 
  SparklesIcon,
  ClockIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CheckIcon,
  ArrowRightIcon,
  PlayIcon,
  StarIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

const LandingPage = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isGhana, setIsGhana] = useState(false);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  // Removed auto-redirect - let users stay on landing page even when logged in

  useEffect(() => {
    const fetchDataAndDetectCountry = async () => {
      try {
        // Fetch packages and detect country in parallel
        const [packagesResponse, locationResponse] = await Promise.all([
          api.getPackages(),
          fetch('https://ipapi.co/json/')
        ]);

        // Handle country detection first
        let detectedIsGhana = false;
        try {
          const locationData = await locationResponse.json();
          if (locationData.country_code === 'GH') {
            detectedIsGhana = true;
          }
        } catch (locationError) {
          console.log('Error detecting location:', locationError);
        }

        setIsGhana(detectedIsGhana);

        // Handle packages with the detected location info
        if (packagesResponse.data.success) {
          const packages = packagesResponse.data.packages.map(pkg => ({
            id: pkg.id,
            name: pkg.name,
            price: detectedIsGhana ? pkg.price_local : pkg.price_usd,
            description: pkg.description,
            features: pkg.features,
            recommended: pkg.is_recommended,
            cta: pkg.cta_text
          }));
          setPricingPlans(packages);
        }
      } catch (error) {
        console.log('Error fetching data:', error);
        // Fallback to default values
        setIsGhana(false);
        // Fallback pricing if API fails
        setPricingPlans([
          {
            id: 'starter',
            name: 'Starter',
            price: 9.99,
            description: 'Perfect for small businesses',
            features: ['1 WhatsApp Agent', '100 messages/month', 'Basic AI responses'],
            recommended: false,
            cta: 'Start Free Trial'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchDataAndDetectCountry();
  }, []);

  const formatPrice = (price) => {
    if (isGhana) {
      return `₵${Number(price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `$${Number(price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const features = [
    {
      icon: ClockIcon,
      title: "Connect in Minutes",
      description: "Set up your Snwolley Business API in under 5 minutes. No complex integrations or lengthy approval processes."
    },
    {
      icon: SparklesIcon,
      title: "AI-Powered Responses",
      description: "Advanced AI handles customer inquiries 24/7 with human-like conversations and intelligent routing."
    },
    {
      icon: ShieldCheckIcon,
      title: "Enterprise Security",
      description: "Bank-level security with end-to-end encryption, SOC 2 compliance, and per-client isolation."
    },
    {
      icon: ChartBarIcon,
      title: "Real-time Analytics",
      description: "Deep insights into customer interactions, response times, and engagement metrics."
    }
  ];

  const testimonials = [
    {
      name: "Ama Serwaa",
      role: "CEO",
      company: "Startup",
      avatar: `${process.env.PUBLIC_URL}/Snwolley_Logo.png`,
      content: "Snwolley has revolutionized how we engage with customers in Ghana. The ease of setup and AI accuracy is unmatched!",
      rating: 5
    },
    {
      name: "Kwame Asare",
      role: "Founder",
      company: "Self Employed",
      avatar: "https://i.pravatar.cc/150?img=7", // Placeholder for a Ghanaian man
      content: "Setting up our WhatsApp support was a breeze. Our customers love the instant responses, and it's boosted our sales significantly.",
      rating: 3
    },
    {
      name: "Adwoa Mensah",
      role: "Operations Lead",
      company: "Venture Capital",
      avatar: `${process.env.PUBLIC_URL}/Snwolley_Logo.png`, // Placeholder for a Ghanaian woman
      content: "Snwolley's platform perfectly understands the nuances of our local market. It feels like having a dedicated Ghanaian support team 24/7.",
      rating: 5
    },
    {
      name: "Sarah Johnson",
      role: "CEO",
      company: "TechStart Solutions",
      avatar: `${process.env.PUBLIC_URL}/Snwolley_Logo.png`,
      content: "The AI responses are so natural that our customers can't tell they're talking to a bot. We've reduced response time by 80% and increased customer satisfaction.",
      rating: 5
    },
    {
      name: "Michael Jonathan",
      role: "Marketing Director",
      company: "Digital Growth Agency",
      avatar: `${process.env.PUBLIC_URL}/Snwolley_Logo.png`,
      content: "Integration was seamless and the analytics dashboard gives us incredible insights into customer behavior. Best investment we've made this year.",
      rating: 5
    },
    {
      name: "Grace Sylvia",
      role: "Customer Success Manager",
      company: "FinTech Solutions",
      avatar: "https://i.pravatar.cc/150?img=20",
      content: "Our customer queries are handled 24/7 now. The AI even handles complex financial inquiries with accuracy. Our team can focus on strategic work.",
      rating: 5
    }
  ];



  const stats = [
    { value: '10M+', label: 'Messages Processed' },
    { value: '500+', label: 'Happy Customers' },
    { value: '99.9%', label: 'Uptime Guarantee' },
    { value: '5min', label: 'Average Setup Time' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <img src={`${process.env.PUBLIC_URL}/Snwolley_Logo.png`} alt="Snwolley Logo" className="h-8 w-8" />
              <span className="ml-2 text-xl font-bold text-gray-900">Snwolley</span>
            </div>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors">Features</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 transition-colors">Pricing</a>
              <a href="#testimonials" className="text-gray-600 hover:text-gray-900 transition-colors">Reviews</a>
              <a href="#docs" className="text-gray-600 hover:text-gray-900 transition-colors">Docs</a>
              {!authLoading && !isAuthenticated && (
                <>
                  <Link 
                    to="/login" 
                    className="text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link 
                    to="/signup" 
                    className="bg-gradient-to-r from-whatsapp-500 to-green-600 text-white px-4 py-2 rounded-lg hover:from-whatsapp-600 hover:to-green-700 transition-all duration-200 transform hover:scale-105"
                  >
                    Get Started
                  </Link>
                </>
              )}
              {!authLoading && isAuthenticated && (
                <Link 
                  to="/dashboard" 
                  className="bg-gradient-to-r from-whatsapp-500 to-green-600 text-white px-4 py-2 rounded-lg hover:from-whatsapp-600 hover:to-green-700 transition-all duration-200 transform hover:scale-105"
                >
                  Go to Dashboard
                </Link>
              )}
             
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-whatsapp-50 via-green-50 to-emerald-50 py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8 items-center">
            <div className="lg:col-span-6">
              <div className="text-center lg:text-left">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Customer Service Agent Made Simple{' '}
                  <span className="inline-block relative text-transparent bg-clip-text bg-gradient-to-r from-whatsapp-500 to-green-600 animate-bounce-slow">
                    On WhatsApp
                  </span>
                </h1>
                <p className="mt-6 text-xl text-gray-600 max-w-3xl">
                  Build, deploy, and scale Snwolley Business solutions in minutes. 
                  Connect customers, automate with AI, and grow your business effortlessly.
                </p>
                
                {/* CTA Buttons */}
                <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <Link
                    to={!authLoading && isAuthenticated ? "/dashboard" : "/signup"}
                    className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-gradient-to-r from-whatsapp-500 to-green-600 rounded-xl hover:from-whatsapp-600 hover:to-green-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    {!authLoading && isAuthenticated ? "Go to Dashboard" : "Start Free Trial"}
                    <ArrowRightIcon className="ml-2 h-5 w-5" />
                  </Link>
                  <button className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:border-gray-400 transition-all duration-200">
                    <PlayIcon className="mr-2 h-5 w-5" />
                    Watch Demo
                  </button>
                </div>

                {/* Trust Indicators */}
                <div className="mt-12">
                  <p className="text-sm text-gray-500 mb-4">Join 500+ businesses scaling with WhatsApp</p>
                  <div className="flex items-center justify-center lg:justify-start space-x-6">
                    <div className="flex items-center space-x-2 bg-white rounded-lg px-3 py-2 shadow-sm">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-gray-700">Live API Status</span>
                    </div>
                    <div className="flex items-center space-x-2 bg-white rounded-lg px-3 py-2 shadow-sm">
                      <span className="text-xs font-bold text-green-600">99.9%</span>
                      <span className="text-xs font-medium text-gray-700">Uptime</span>
                    </div>
                    <div className="flex items-center space-x-2 bg-white rounded-lg px-3 py-2 shadow-sm">
                      <span className="text-xs font-bold text-blue-600">24/7</span>
                      <span className="text-xs font-medium text-gray-700">Support</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Image/Animation */}
            <div className="mt-16 lg:mt-0 lg:col-span-6">
              <div className="relative px-8 py-8">
                <WhatsAppChatAnimation />
              </div>
            </div>
          </div>
        </div>

        {/* Background Decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-10 -left-10 w-40 h-40 bg-whatsapp-200 rounded-full opacity-20 animate-pulse"></div>
          <div className="absolute top-1/3 -right-20 w-60 h-60 bg-green-200 rounded-full opacity-20 animate-pulse delay-1000"></div>
          <div className="absolute -bottom-20 left-1/4 w-80 h-80 bg-emerald-200 rounded-full opacity-20 animate-pulse delay-2000"></div>
        </div>
      </section>

      {/* Interactive Business Showcase */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              See Snwolley Business API in Action
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Watch how businesses across industries leverage our API to automate customer communication, 
              boost engagement, and scale their operations through WhatsApp.
            </p>
          </div>
          
          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Main Animation - Spans 2 columns */}
            <div className="lg:col-span-2 md:col-span-2 h-96">
              <WhatsAppBusinessAnimation />
            </div>
            
            {/* Side Stats */}
            <div className="space-y-6">
              {/* Active Businesses */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 h-44 flex flex-col justify-center">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">500+</div>
                  <div className="text-gray-700 font-medium">Active Businesses</div>
                  <div className="text-sm text-gray-500 mt-2">
                    From startups to enterprises
                  </div>
                </div>
              </div>
              
              {/* Messages Sent */}
              <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl border border-gray-200 p-6 h-44 flex flex-col justify-center">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">10M+</div>
                  <div className="text-gray-700 font-medium">Messages Delivered</div>
                  <div className="text-sm text-gray-500 mt-2">
                    99.9% delivery rate
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Use Cases Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center hover:shadow-lg transition-shadow">
              <div className="text-2xl mb-2">🛒</div>
              <div className="text-sm font-medium text-gray-700">E-Commerce</div>
              <div className="text-xs text-gray-500 mt-1">Order updates & support</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center hover:shadow-lg transition-shadow">
              <div className="text-2xl mb-2">🍕</div>
              <div className="text-sm font-medium text-gray-700">Restaurants</div>
              <div className="text-xs text-gray-500 mt-1">Menu & reservations</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center hover:shadow-lg transition-shadow">
              <div className="text-2xl mb-2">🏥</div>
              <div className="text-sm font-medium text-gray-700">Healthcare</div>
              <div className="text-xs text-gray-500 mt-1">Appointments & reminders</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center hover:shadow-lg transition-shadow">
              <div className="text-2xl mb-2">🏢</div>
              <div className="text-sm font-medium text-gray-700">SaaS</div>
              <div className="text-xs text-gray-500 mt-1">User onboarding & support</div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-gray-900">{stat.value}</div>
                <div className="mt-2 text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Everything you need to scale
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Powerful features designed to help businesses of all sizes deliver exceptional customer experiences through WhatsApp.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {features.map((feature, index) => (
              <div key={index} className="flex">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 bg-gradient-to-r from-whatsapp-500 to-green-600 rounded-xl">
                    <feature.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Loved by businesses worldwide
            </h2>
            <p className="text-xl text-gray-600">
              Don't just take our word for it. Here's what our customers have to say.
            </p>
          </div>

          {/* Animated Carousel */}
          <div className="testimonials-container">
            <div className="testimonials-track">
              {/* Duplicate testimonials for seamless loop */}
              {[...testimonials, ...testimonials].map((testimonial, index) => (
                <div key={index} className="testimonial-card bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="flex items-center space-x-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed">"{testimonial.content}"</p>
                  <div className="flex items-center">
                    <img 
                      src={testimonial.avatar} 
                      alt={testimonial.name}
                      className="h-12 w-12 rounded-full object-cover"
                    />
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                      <p className="text-sm text-gray-600">{testimonial.role} at {testimonial.company}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-xl text-gray-600">
              Choose the perfect plan for your business. No hidden fees, cancel anytime.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {loading ? (
              // Loading skeleton
              [1, 2, 3].map((index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg animate-pulse">
                  <div className="text-center mb-8">
                    <div className="h-8 bg-gray-200 rounded mb-2"></div>
                    <div className="h-16 bg-gray-200 rounded mb-4"></div>
                    <div className="h-6 bg-gray-200 rounded"></div>
                  </div>
                  <div className="space-y-3 mb-8">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="h-4 bg-gray-200 rounded"></div>
                    ))}
                  </div>
                  <div className="h-12 bg-gray-200 rounded"></div>
                </div>
              ))
            ) : (
              pricingPlans.map((plan) => (
              <div 
                key={plan.id} 
                className={`bg-white rounded-2xl p-8 shadow-lg relative ${
                  plan.recommended ? 'ring-2 ring-whatsapp-500 transform scale-105' : ''
                }`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-whatsapp-500 to-green-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-4">{plan.description}</p>
                  <div className="flex items-center justify-center">
                    <span className="text-4xl font-bold text-gray-900">{formatPrice(plan.price)}</span>
                    <span className="text-gray-600 ml-2">/month</span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {plan.cta === 'Contact Sales' ? (
                  <a
                    href="mailto:marketing@npontu.com?subject=Enterprise Plan Inquiry&body=Hi, I'm interested in learning more about your Enterprise plan and custom solutions."
                    className={`w-full py-3 px-6 rounded-lg font-medium text-center block transition-all duration-200 ${
                      plan.recommended
                        ? 'bg-gradient-to-r from-whatsapp-500 to-green-600 text-white hover:from-whatsapp-600 hover:to-green-700 transform hover:scale-105'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {plan.cta}
                  </a>
                ) : (
                  <Link
                    to={`/signup?plan=${plan.id}`}
                    className={`w-full py-3 px-6 rounded-lg font-medium text-center block transition-all duration-200 ${
                      plan.recommended
                        ? 'bg-gradient-to-r from-whatsapp-500 to-green-600 text-white hover:from-whatsapp-600 hover:to-green-700 transform hover:scale-105'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {plan.cta}
                  </Link>
                )}
              </div>
            ))
            )}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600">
              Need a custom plan?{' '}
              <a 
                href="mailto:marketing@npontu.com?subject=Custom Plan Inquiry&body=Hi, I'm interested in discussing a custom plan for my business needs." 
                className="text-whatsapp-600 hover:text-whatsapp-700 font-medium"
              >
                Contact our sales team
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-whatsapp-500 to-green-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to transform your customer communication?
          </h2>
          <p className="text-xl text-whatsapp-100 mb-10">
            Join hundreds of businesses already using Snwolley to deliver exceptional customer experiences.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={!authLoading && isAuthenticated ? "/dashboard" : "/signup"}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-whatsapp-600 bg-white rounded-xl hover:bg-gray-50 transform hover:scale-105 transition-all duration-200 shadow-lg"
            >
              {!authLoading && isAuthenticated ? "Go to Dashboard" : "Start Free Trial"}
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
            <a
              href="mailto:marketing@npontu.com?subject=Demo Request&body=Hi, I would like to schedule a demo of your Snwolley Business API platform. Please let me know your available times."
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white border-2 border-white rounded-xl hover:bg-white hover:text-whatsapp-600 transition-all duration-200"
            >
              Schedule Demo
            </a>
          </div>
          <p className="mt-6 text-sm text-whatsapp-100">
            No credit card required • Free 14-day trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img src={`${process.env.PUBLIC_URL}/Snwolley_Logo.png`} alt="Snwolley Logo" className="h-8 w-8" />
                <span className="ml-2 text-xl font-bold text-white">Snwolley</span>
              </div>
              <p className="text-gray-400 mb-6 max-w-md">
                The most powerful Business API platform. Connect, automate, and scale your customer communication.
              </p>
              <div className="flex space-x-4">
                <a href="https://snwolley.ai" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                  <GlobeAltIcon className="h-6 w-6" />
                </a>
                <a href="mailto:marketing@npontu.com" className="text-gray-400 hover:text-white transition-colors">
                  <EnvelopeIcon className="h-6 w-6" />
                </a>
                <a href="tel:+23355654152" className="text-gray-400 hover:text-white transition-colors">
                  <PhoneIcon className="h-6 w-6" />
                </a>
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#docs" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">API Docs</a></li>
              </ul>
            </div>

            {/* Support Links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><a href="mailto:marketing@npontu.com" className="hover:text-white transition-colors">Contact Us</a></li>
                <li><a href="#privacy" className="hover:text-white transition-colors">Privacy</a></li>
                <li><a href="#terms" className="hover:text-white transition-colors">Terms</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2025 Snwolley. All rights reserved.
            </p>
            <p className="text-gray-400 text-sm mt-4 md:mt-0">
              Made with ❤️ for Africa
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
